import type { PublicTransfer, DownloadResponse } from './types'

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.carbon.to'

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export async function getTransferInfo(transferId: string): Promise<PublicTransfer> {
  const response = await fetch(`${API_BASE}/api/transfers/${transferId}/public`)

  if (!response.ok) {
    if (response.status === 404) {
      throw new ApiError('Transfer not found', 404, 'NOT_FOUND')
    }
    if (response.status === 410) {
      throw new ApiError('Transfer has expired', 410, 'EXPIRED')
    }
    throw new ApiError('Failed to fetch transfer info', response.status)
  }

  const data = await response.json()
  return data.transfer
}

export async function requestDownload(
  transferId: string,
  passwordHash?: string
): Promise<DownloadResponse> {
  const response = await fetch(`${API_BASE}/api/transfers/${transferId}/download`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      password_hash: passwordHash,
    }),
  })

  if (!response.ok) {
    if (response.status === 401) {
      throw new ApiError('Invalid password', 401, 'INVALID_PASSWORD')
    }
    if (response.status === 429) {
      throw new ApiError('Download limit reached', 429, 'LIMIT_REACHED')
    }
    throw new ApiError('Failed to request download', response.status)
  }

  return response.json()
}

export async function downloadEncryptedFile(
  downloadUrl: string,
  onProgress?: (progress: number) => void
): Promise<ArrayBuffer> {
  const response = await fetch(downloadUrl)

  if (!response.ok) {
    throw new ApiError('Failed to download file', response.status)
  }

  const contentLength = response.headers.get('Content-Length')
  const total = contentLength ? parseInt(contentLength, 10) : 0

  if (!response.body) {
    throw new ApiError('No response body', 500)
  }

  const reader = response.body.getReader()
  const chunks: Uint8Array[] = []
  let received = 0

  while (true) {
    const { done, value } = await reader.read()

    if (done) break

    chunks.push(value)
    received += value.length

    if (total > 0 && onProgress) {
      onProgress(received / total)
    }
  }

  // Combine chunks into single ArrayBuffer
  const combined = new Uint8Array(received)
  let offset = 0
  for (const chunk of chunks) {
    combined.set(chunk, offset)
    offset += chunk.length
  }

  return combined.buffer
}
